import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Multi-select category picker with search + chips.
 * value: string[] of category ids
 */
const SearchableCategorySelect = ({
  categories = [],
  value,
  onChange,
  required = false,
  multiple = true,
  label = 'Categories'
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedIds = useMemo(() => {
    if (multiple) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (value) return [value];
      return [];
    }
    return value ? [value] : [];
  }, [value, multiple]);

  const selectedCats = useMemo(
    () => selectedIds.map((id) => categories.find((c) => c.id === id)).filter(Boolean),
    [categories, selectedIds]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const base = categories.filter((cat) => !selectedIds.includes(cat.id));
    if (!term) return base;
    return base.filter((cat) => cat.name.toLowerCase().includes(term));
  }, [categories, query, selectedIds]);

  const emit = (ids) => {
    if (multiple) onChange(ids);
    else onChange(ids[0] || '');
  };

  const handleSelect = (cat) => {
    if (multiple) {
      emit([...selectedIds, cat.id]);
      setQuery('');
      setOpen(true);
    } else {
      emit([cat.id]);
      setQuery('');
      setOpen(false);
    }
  };

  const removeId = (id) => {
    emit(selectedIds.filter((x) => x !== id));
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none">
        {label} {required ? '*' : ''}
      </label>
      <div className="border border-gray-300 rounded-lg focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/30 transition-all min-h-[52px] px-3 py-2">
        {selectedCats.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {selectedCats.map((cat) => (
              <span
                key={cat.id}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md bg-orange-50 text-orange-800 border border-orange-200"
              >
                {cat.name}
                <button
                  type="button"
                  onClick={() => removeId(cat.id)}
                  className="text-orange-500 hover:text-orange-800"
                  aria-label={`Remove ${cat.name}`}
                >
                  <i className="fas fa-times text-[10px]" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <i className="fas fa-search absolute left-1 top-2.5 text-gray-400 pointer-events-none text-sm" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={selectedCats.length ? 'Add another category…' : 'Search categories…'}
            className="w-full py-2 pl-7 pr-2 border-0 rounded-lg focus:outline-none bg-transparent text-sm"
            autoComplete="off"
            aria-required={required && selectedIds.length === 0}
          />
        </div>
      </div>
      {required && (
        <input
          tabIndex={-1}
          className="sr-only"
          required={selectedIds.length === 0}
          value={selectedIds.join(',')}
          onChange={() => {}}
        />
      )}

      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-gray-500">
              {categories.length === selectedIds.length ? 'All categories selected' : 'No categories match'}
            </li>
          ) : (
            filtered.map((cat) => (
              <li key={cat.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(cat)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors text-gray-800"
                >
                  {cat.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableCategorySelect;
